import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import DatePicker from "../../components/DatePicker"; // Ensure the path is correct

const formFields = [
  {
    name: "name",
    label: "Name",
    type: "text",
    validation: { required: "Name is required" },
  },
  {
    name: "gpId",
    label: "GP ID",
    type: "text",
    validation: { required: "GP ID is required" },
  },
  {
    name: "gender",
    label: "Gender",
    type: "select",
    options: ["Male", "Female"],
    validation: { required: "Gender is required" },
  },
  {
    name: "age",
    label: "Age",
    type: "text",
    validation: {
      required: "Age is required",
      pattern: { value: /^[0-9]+$/, message: "Age must be a valid number" },
    },
  },
  {
    name: "tier",
    label: "Tier",
    type: "select",
    options: ["Gold", "Silver", "Platinum"],
    validation: { required: "Tier is required" },
  },
  {
    name: "primaryProduct",
    label: "Primary Product",
    type: "select",
    options: ["Products", "Brands", "Both"],
    validation: { required: "Primary Product is required" },
  },
  {
    name: "lastLeadCreation",
    label: "Last Lead Creation",
    type: "date",
    validation: { required: "Last Lead Creation date is required" },
  },
  {
    name: "lastLeadSale",
    label: "Last Lead Sale",
    type: "date",
    validation: { required: "Last Lead Sale date is required" },
  },
  {
    name: "lastActiveDate",
    label: "Last Active Date",
    type: "date",
    validation: { required: "Last Active Date is required" },
  },
  { name: "sessionId", label: "Session ID", type: "text", validation: {} },
];

const GromoGuruForm = () => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm();
  const [isLoading, setIsLoading] = useState(false);

  const initiateChat = async (payload: any) => {
    try {
      const response: any = await fetch(
        `https://guru-stg.gromo.in/api/v1/guru/initiate/chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      const responseData = await response.json();
      sessionStorage.setItem("sessionId", responseData?.sessionId);
      responseData?.previousChats && sessionStorage.setItem("prevMsg",JSON.stringify(responseData?.previousChats))
      window.location.href = `/guru-ai-bot/chat?message=${encodeURIComponent(
        responseData.message
      )}`;
    } catch (error) {
      console.error("Error sending API request:", error);
    }
  };

  const onSubmit = async (data: any) => {
    setIsLoading(true);
    const payload = {
      entityId: data.gpId,
      entityType: "GP",
      sessionId: data.sessionId,
      entityData: {
        demographicData: {
          gender: data.gender.toLowerCase(),
          age: data.age,
          name: data.name,
        },
        performanceData: {
          tier: data.tier.toLowerCase(),
          primaryProduct: data.primaryProduct.toLowerCase(),
          lastLeadCreationDate: new Date(
            data.lastLeadCreation
          ).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }),
          lastSaleDate: new Date(data.lastLeadSale).toLocaleDateString(
            "en-GB",
            { day: "2-digit", month: "short", year: "numeric" }
          ),
          lastActiveTime: data.lastActiveDate || "",
        },
      },
    };
    sessionStorage.setItem("entityId", data.gpId);
    sessionStorage.setItem("formData", JSON.stringify(data));
    await initiateChat(payload);
    setIsLoading(false);
  };

  useEffect(() => {
    const savedData = sessionStorage.getItem("formData");
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      Object.keys(parsedData).forEach((key) => {
        setValue(key, parsedData[key]);
      });
    }
  }, [setValue]);

  const renderField = (field: any) => {
    const error = errors[field.name]?.message;

    switch (field.type) {
      case "text":
        return (
          <Controller
            key={field.name}
            name={field.name}
            control={control}
            defaultValue=""
            rules={field.validation}
            render={({ field: controllerField }) => (
              <TextField
                {...controllerField}
                label={field.label}
                type={field.name === "age" ? "number" : "text"}
                fullWidth
                margin="normal"
                error={!!error}
                helperText={error as any}
              />
            )}
          />
        );

      case "select":
        return (
          <Controller
            key={field.name}
            name={field.name}
            control={control}
            defaultValue=""
            rules={field.validation}
            render={({ field: controllerField }) => (
              <TextField
                {...controllerField}
                label={field.label}
                select
                fullWidth
                margin="normal"
                error={!!error}
                helperText={error as any}
              >
                {field.options.map((option: string) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        );

      case "date":
        return (
          <Controller
            key={field.name}
            name={field.name}
            control={control}
            defaultValue=""
            rules={field.validation}
            render={({ field: controllerField }) => (
              <DatePicker
                placeholder={field.label}
                style={{ marginBottom: "16px" }}
                value={controllerField.value}
                setDate={(date) => controllerField.onChange(date)}
                error={error as any}
              />
            )}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: 3,
        backgroundColor: "#f4f4f4",
      }}
    >
      <Box
        sx={{
          maxWidth: 720,
          width: "100%",
          padding: 4,
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom textAlign="center">
          Gromo Guru
        </Typography>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              {formFields.map((field) => (
                <Grid item xs={12} sm={6} key={field.name}>
                  {renderField(field)}
                </Grid>
              ))}
            </Grid>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{
                backgroundColor: "#25d366",
                "&:hover": { backgroundColor: "#1db954" },
                borderRadius: "50px",
                padding: "10px 20px",
                fontWeight: 600,
                mt:3
              }}
            >
              Ask Me!!
            </Button>
          </form>
        )}
      </Box>
    </Box>
  );
};

export default GromoGuruForm;
